import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Image as ImageIcon, TestTube } from "lucide-react";
import { toast } from "sonner";
import {
  fetchProducts,
  formatGBP,
  adminCreateProduct,
  adminDeleteProduct,
  adminUpdateProduct,
  resolveImageUrl,
} from "../lib/api";

const blankProduct = {
  sku: "",
  name: "",
  dosage: "",
  price_gbp: 0,
  stock: 0,
  short_description: "",
  description: "",
  cas_number: "",
  molecular_weight: "",
  purity: "≥99%",
  category: "Peptide",
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [draft, setDraft] = useState({}); // id -> { stock?, price_gbp? }
  const [showNew, setShowNew] = useState(false);
  const [newProduct, setNewProduct] = useState(blankProduct);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchProducts();
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setDraftVal = (id, field, value) => {
    setDraft((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveInline = async (p) => {
    const d = draft[p.id] || {};
    const patch = {};
    if (d.stock !== undefined && Number(d.stock) !== p.stock) patch.stock = Number(d.stock);
    if (d.price_gbp !== undefined && Number(d.price_gbp) !== p.price_gbp)
      patch.price_gbp = Number(d.price_gbp);
    if (Object.keys(patch).length === 0) return;
    setBusyId(p.id);
    try {
      const updated = await adminUpdateProduct(p.id, patch);
      setProducts((prev) => prev.map((x) => (x.id === p.id ? updated : x)));
      setDraft((prev) => ({ ...prev, [p.id]: {} }));
      toast.success(`Saved ${p.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Permanently delete ${p.name} (${p.dosage})?\n\nThis cannot be undone.`))
      return;
    setBusyId(p.id);
    try {
      await adminDeleteProduct(p.id);
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success(`Deleted ${p.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!newProduct.sku.trim() || !newProduct.name.trim() || !newProduct.dosage.trim()) {
      toast.error("SKU, name and dosage are required.");
      return;
    }
    setCreating(true);
    try {
      const created = await adminCreateProduct({
        ...newProduct,
        price_gbp: Number(newProduct.price_gbp) || 0,
        stock: Number(newProduct.stock) || 0,
      });
      setProducts((prev) => [...prev, created]);
      toast.success(`Created ${created.name}`);
      setShowNew(false);
      setNewProduct(blankProduct);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create product");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-[#64748B]">
          {loading ? "Loading…" : `${products.length} products`} · Click any field to edit.
        </p>
        <button
          data-testid="product-new-btn"
          onClick={() => setShowNew((v) => !v)}
          className="inline-flex items-center gap-2 bg-[#1B3A5C] text-white hover:bg-[#254D7A] px-4 py-2 text-sm font-medium tracking-wide transition-colors"
        >
          <Plus className="w-4 h-4" /> {showNew ? "Cancel" : "New product"}
        </button>
      </div>

      {showNew && (
        <form
          onSubmit={create}
          data-testid="product-new-form"
          className="border border-[#E2E8F0] bg-[#F8FAFC] p-6 mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {[
            ["sku", "SKU *", "text", "SB-XYZ-10"],
            ["name", "Name *", "text", "Peptide name"],
            ["dosage", "Dosage *", "text", "10mg"],
            ["price_gbp", "Price (GBP) *", "number", "0.00"],
            ["stock", "Stock *", "number", "20"],
            ["category", "Category", "text", "Peptide"],
            ["cas_number", "CAS number", "text", "—"],
            ["molecular_weight", "Mol. weight", "text", "—"],
            ["purity", "Purity", "text", "≥99%"],
          ].map(([key, label, type, ph]) => (
            <label key={key} className="block">
              <span className="sb-mono-label">{label}</span>
              <input
                data-testid={`new-${key}`}
                type={type}
                step={type === "number" ? "any" : undefined}
                value={newProduct[key] ?? ""}
                onChange={(e) => setNewProduct({ ...newProduct, [key]: e.target.value })}
                placeholder={ph}
                className="mt-2 block w-full border border-[#E2E8F0] focus:border-[#2C8C9C] focus:outline-none px-3 py-2 text-sm bg-white"
              />
            </label>
          ))}
          <label className="block sm:col-span-2 lg:col-span-3">
            <span className="sb-mono-label">Short description</span>
            <input
              data-testid="new-short_description"
              type="text"
              value={newProduct.short_description}
              onChange={(e) => setNewProduct({ ...newProduct, short_description: e.target.value })}
              className="mt-2 block w-full border border-[#E2E8F0] focus:border-[#2C8C9C] focus:outline-none px-3 py-2 text-sm bg-white"
            />
          </label>
          <label className="block sm:col-span-2 lg:col-span-3">
            <span className="sb-mono-label">Description</span>
            <textarea
              data-testid="new-description"
              rows={3}
              value={newProduct.description}
              onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
              className="mt-2 block w-full border border-[#E2E8F0] focus:border-[#2C8C9C] focus:outline-none px-3 py-2 text-sm bg-white"
            />
          </label>
          <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
            <button
              type="submit"
              disabled={creating}
              data-testid="new-submit"
              className="bg-[#1B3A5C] text-white hover:bg-[#254D7A] px-6 py-2.5 text-sm font-medium tracking-wide transition-colors disabled:opacity-50"
            >
              {creating ? "Creating…" : "Create product"}
            </button>
          </div>
        </form>
      )}

      <div className="border border-[#E2E8F0] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <tr>
              <th className="text-left py-3 px-4 sb-mono-label !text-[#1B3A5C]">Image</th>
              <th className="text-left py-3 px-4 sb-mono-label !text-[#1B3A5C]">Product</th>
              <th className="text-right py-3 px-4 sb-mono-label !text-[#1B3A5C]">Price (GBP)</th>
              <th className="text-right py-3 px-4 sb-mono-label !text-[#1B3A5C]">Stock</th>
              <th className="text-right py-3 px-4 sb-mono-label !text-[#1B3A5C]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && products.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-12 text-[#64748B]">
                  No products yet.
                </td>
              </tr>
            )}
            {products.map((p) => {
              const d = draft[p.id] || {};
              const stockVal = d.stock !== undefined ? d.stock : p.stock;
              const priceVal = d.price_gbp !== undefined ? d.price_gbp : p.price_gbp;
              const dirty =
                (d.stock !== undefined && Number(d.stock) !== p.stock) ||
                (d.price_gbp !== undefined && Number(d.price_gbp) !== p.price_gbp);
              const img = resolveImageUrl(p.image_url);
              return (
                <tr key={p.id} className="border-b border-[#E2E8F0] last:border-b-0 align-middle">
                  <td className="py-3 px-4">
                    <div className="w-14 h-14 border border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-center overflow-hidden">
                      {img ? (
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <TestTube className="w-7 h-7 text-[#2C8C9C]/40" strokeWidth={1.25} />
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-mono text-xs text-[#64748B]">{p.sku}</div>
                    <div className="font-medium text-[#1B3A5C]">{p.name}</div>
                    <div className="text-xs text-[#64748B] font-mono">{p.dosage} · {p.category}</div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-1 border border-[#E2E8F0] bg-white px-2 py-1">
                      <span className="text-xs text-[#64748B]">£</span>
                      <input
                        data-testid={`price-${p.sku}`}
                        type="number"
                        step="0.01"
                        value={priceVal}
                        onChange={(e) => setDraftVal(p.id, "price_gbp", e.target.value)}
                        className="w-20 text-right font-mono text-sm bg-white outline-none"
                      />
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <input
                      data-testid={`stock-${p.sku}`}
                      type="number"
                      min={0}
                      value={stockVal}
                      onChange={(e) => setDraftVal(p.id, "stock", e.target.value)}
                      className="w-20 text-right font-mono text-sm border border-[#E2E8F0] px-2 py-1 bg-white outline-none focus:border-[#2C8C9C]"
                    />
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      {dirty && (
                        <button
                          type="button"
                          onClick={() => saveInline(p)}
                          disabled={busyId === p.id}
                          data-testid={`save-${p.sku}`}
                          className="px-3 py-1.5 text-xs font-medium bg-[#2C8C9C] text-white hover:bg-[#39B3C6] transition-colors disabled:opacity-50"
                        >
                          Save
                        </button>
                      )}
                      <Link
                        to={`/admin/products/${p.id}`}
                        data-testid={`edit-${p.sku}`}
                        className="inline-flex items-center gap-1.5 border border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white px-3 py-1.5 text-xs font-medium tracking-wide transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </Link>
                      <button
                        type="button"
                        onClick={() => remove(p)}
                        disabled={busyId === p.id}
                        data-testid={`delete-${p.sku}`}
                        className="inline-flex items-center gap-1.5 border border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white px-3 py-1.5 text-xs font-medium tracking-wide transition-colors disabled:opacity-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-[#64748B] flex items-center gap-2">
        <ImageIcon className="w-3.5 h-3.5" /> Product images are uploaded on the Edit page (drag &amp; drop).
      </p>
    </div>
  );
};

export default AdminProducts;
